import React from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from '../shared/TailwindComponents/Skeleton';

interface SidePanelProps {
  day: string | null;
  onClose: () => void;
  data: any;
  isLoading: boolean;
  stats: {
    clocked_hours: number;
    negative_hours: number;
    overtime: number;
    total_hours_worked: number;
    tasks: string[];
    casesWorkedOn: number;
  } | null;
}

const TimekeepingSidePanel: React.FC<SidePanelProps> = ({ day, onClose, data, isLoading, stats }) => {
  const { t } = useTranslation();

  if (!data) {
    return (
      <div className="employee-day--details">
        <div className="non-access">
          {t('No data available for')} {day}
        </div>
      </div>
    );
  }

  const formatDecimalHours = (decimalHours: number): string => {
    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  return (
    <div className="employee-day--details">
      {isLoading ? (
        <Skeleton variant="list" rows={4} />
      ) : (
        <>
          <h3>
            {t('Details for')} {day}
          </h3>

          {stats && (
            <div className="stats-container">
              <div className="stat-item">
                <span className="stat-title">{t('Tasks completed')}</span>
                <span className="stat-value">{stats.tasks.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-title">{t('Cases worked on')}</span>
                <span className="stat-value">{stats.casesWorkedOn || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-title">{t('Clocked hours')}</span>
                <span className="stat-value">{formatDecimalHours(stats.clocked_hours)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-title">{t('Negative hours')}</span>
                <span className="stat-value">{formatDecimalHours(stats.negative_hours)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-title">{t('Overtime')}</span>
                <span className="stat-value">{formatDecimalHours(stats.overtime)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-title">{t('Total hours worked')}</span>
                <span className="stat-value">{formatDecimalHours(stats.total_hours_worked)}</span>
              </div>
            </div>
          )}

          <div className="employee-day--tasks-details">
            <h4>{t('Tasks completed')}</h4>
            {data.map((task: any, index: number) => {
              return (
                <React.Fragment key={index}>
                  {task.task_id !== '' ? (
                    <ul style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <li>
                          <span>{task.task_detail.TaskTitle}</span>
                        </li>
                        <li>
                          <span
                            style={{
                              padding: '3px 8px',
                              borderRadius: 4,
                              backgroundColor: '#1e1f21',
                              color: '#64d2ff',
                            }}
                          >
                            {task.task_detail.Status}
                          </span>
                        </li>
                      </div>
                      <li>
                        <span>{formatDecimalHours(parseFloat(task.duration))} {t('Hours spent')}</span>
                      </li>
                      <li>
                        <span
                          style={{
                            padding: 8,
                            backgroundColor: '#1e1f21',
                            flex: 1,
                            minHeight: 50
                          }}
                        >
                          {task.task_detail.TaskDescription}
                        </span>
                      </li>
                    </ul>
                  ) : (
                    <div className='non-access'>{t('No tasks worked on')}</div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="employee-day--cases-details">
            <h4>{t('Cases worked on')}</h4>
            {data.map((task: any, index: number) => {
              return (
                <React.Fragment key={index}>
                  {task.case_id !== '' ? (
                    <ul style={{ marginBottom: '10px' }}>
                      <li>
                        <strong>{t('Case type')}:</strong> <span>{task.case_detail.CaseType}</span>
                      </li>
                      <li>
                        <strong>{t('Case description')}:</strong>{' '}
                        <span>{task.case_detail.Description}</span>
                      </li>
                      <li>
                        <strong>{t('Desired outcome')}:</strong>{' '}
                        <span>{task.case_detail.DesiredOutcome}</span>
                      </li>
                      <li>
                        <strong>{t('Expected outcome')}:</strong>{' '}
                        <span>{task.case_detail.ExpectedOutcome}</span>
                      </li>
                      <li>
                        <strong>{t('Status')}:</strong> <span>{task.case_detail.Status}</span>
                      </li>
                      <li>
                        <strong>{t('Total work hours')}:</strong>{' '}
                        <span>{formatDecimalHours(Number.parseFloat(task.duration))}</span>
                      </li>
                    </ul>
                  ) : (
                    <div className='non-access'>{t('No cases worked on')}</div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default TimekeepingSidePanel;
